/**
 * @file TypeNumberFields.tsx
 * @description Числа вкладки «Шрифты» (phase19.16, партия SB120, этап 2;
 * site-builder.md SB10, SB120; проект uiux-sb120-stage2 §2 «Шрифты», §5;
 * данные db-design-sb120-stage2 §1 `type.headingScale`, `type.eyebrowTracking`):
 *
 * ```
 * Размер заголовков                                   ↺ ×1
 * ├──────────●──────────┤ [ 1,125 ] ×   [Мельче 0,875][Обычный 1][Крупнее 1,125]
 *  Заголовок раздела: ПК 34 → 38 px, телефон 24 → 27 px
 * Разрядка надписей капсом                            ↺ 0,08 em
 * ├───●─────────────────┤ [ 0,20 ] em   [Обычная 0][Широкая 0,08][Очень широкая 0,2]
 *  НАДПИСЬ НАД ЗАГОЛОВКОМ — образец шрифтом надписей
 * ```
 *
 * Вместо прежнего Segmented «Мельче / Обычный / Крупнее» — ползунок; ступени
 * v1 — подсказки. «↺» возвращает умолчание платформы (×1 и 0,08 em).
 */

import { HEADING_SCALE_LABEL, SITE_THEME_LIMITS, type SiteTheme } from "@chaiberi/shared";
import { useState } from "react";

import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { FontSampleText } from "./FontSampleText";
import {
  HEADING_SCALE_PRESETS,
  HEADING_SCALE_RECOMMENDED,
  TRACKING_PRESETS,
  TRACKING_RECOMMENDED,
  headingScaleHint,
} from "../../../lib/site-view/appearance-numbers";
import { formatNumber } from "../../../lib/site-fields/number-input";

type TypeSection = SiteTheme["type"];
type TypePatch = (patch: Partial<TypeSection>) => void;

const { headingScale: HS, eyebrowTracking: ET } = SITE_THEME_LIMITS;
/** Образец надписи капсом — как на сайте. */
const SAMPLE = "Надпись над заголовком".toLocaleUpperCase("ru");

interface ScaleProps {
  value: number;
  onType: TypePatch;
  onPreview?: TypePatch;
}

/** «Размер заголовков» множителем 0,75–1,5 с пересчётом заголовка раздела в px. */
export function HeadingScaleField({ value, onType, onPreview }: ScaleProps) {
  // Подпись «ПК 34 → N px» — по живому значению во время перетаскивания (QA SB120 этап 2); запись сбрасывает.
  const [live, setLive] = useState<number | null>(null);
  return (
    <NumberSliderField
      label={HEADING_SCALE_LABEL}
      value={value}
      inherit={{ value: HS.default, label: "×1" }}
      min={HS.min}
      max={HS.max}
      step={HS.step}
      unit="×"
      display={{ factor: 1, decimals: 3 }}
      presets={HEADING_SCALE_PRESETS}
      recommended={HEADING_SCALE_RECOMMENDED}
      hint={headingScaleHint(live ?? value)}
      onChange={(v) => {
        setLive(null);
        onType({ headingScale: v ?? HS.default });
      }}
      onPreview={(v) => {
        const next = v ?? HS.default;
        setLive(next);
        onPreview?.({ headingScale: next });
      }}
    />
  );
}

interface TrackingProps {
  value: number;
  /** Шрифт надписей капсом (основной или заголовков — `eyebrowFrom`). */
  fontKey: string;
  onType: TypePatch;
  onPreview?: TypePatch;
}

/** «Разрядка надписей капсом» em 0–0,4 с образцом надписи этим шрифтом. */
export function EyebrowTrackingField({ value, fontKey, onType, onPreview }: TrackingProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <NumberSliderField
        label="Разрядка надписей капсом"
        value={value}
        inherit={{ value: ET.default, label: `${formatNumber(ET.default, 2)} em` }}
        min={ET.min}
        max={ET.max}
        step={ET.step}
        unit="em"
        display={{ factor: 1, decimals: 2 }}
        presets={TRACKING_PRESETS}
        recommended={TRACKING_RECOMMENDED}
        onChange={(v) => onType({ eyebrowTracking: v ?? ET.default })}
        onPreview={onPreview ? (v) => onPreview({ eyebrowTracking: v ?? ET.default }) : undefined}
      />
      {/* Разрядка наследуется образцом шрифта; капс — текстом (образец сайта, не подпись кабинета). */}
      <span role="img" aria-label="Образец надписи капсом" className="block min-w-0 rounded-lg bg-surface-low px-3 py-2" style={{ letterSpacing: `${value}em` }}>
        <FontSampleText fontKey={fontKey} text={SAMPLE} size={13} />
      </span>
    </div>
  );
}
